/*
  Ministry Checkup completion status. Admin sees who has completed and who has not.
  Can add more invitees after survey has started.
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, patch } from '../api/client';

const MinistryCheckupCompletionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [addSelected, setAddSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);

  const fetchData = useCallback(async () => {
    const { ok, data: d } = await get(`/api/surveys/${id}/completion`);
    if (ok) setData(d);
  }, [id]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    };
    if (id) run();
  }, [id, fetchData]);

  const loadUsersNotInvited = async () => {
    setShowAddSection(true);
    setUsersLoading(true);
    const { ok, data: userList } = await get('/api/surveys/users');
    setUsersLoading(false);
    if (ok && Array.isArray(userList) && data) {
      const invitedIds = new Set(
        (data.completedUsers || []).map((u) => u.userId).concat((data.notCompletedUsers || []).map((u) => u.userId))
      );
      setUsers(userList.filter((u) => !invitedIds.has(u.id)));
    }
  };

  const handleAddInvitees = async () => {
    if (addSelected.size === 0) return;
    setAdding(true);
    setAddError('');
    const { ok, data: resData } = await patch(`/api/surveys/${id}/invitees`, { addUserIds: Array.from(addSelected) });
    setAdding(false);
    if (ok) {
      setAddSelected(new Set());
      setUsers([]);
      await fetchData();
    } else {
      setAddError(resData?.message || 'Failed to add invitees');
    }
  };

  const toggleAddUser = (uid) => {
    setAddSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (!data) return <div style={{ padding: '2rem' }}>Not found.</div>;

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <PageHeader title="Survey Completion" backTo="/ministry-checkup" backLabel="← Ministry Checkup" />
      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '1rem' }}>
        Track who has completed this survey. Add more invitees if needed.
      </p>
      <button type="button" className="btn-outline" style={{ marginBottom: '1rem' }} onClick={() => navigate('/ministry-checkup')}>
        ← Back
      </button>

      {data.inviteEmailsSentAt && (
        <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Invites sent: {new Date(data.inviteEmailsSentAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </p>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ padding: '1rem', background: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)', minWidth: '120px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{data.percentComplete}%</div>
          <div style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)' }}>Complete</div>
        </div>
        <div style={{ padding: '1rem', background: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)', minWidth: '120px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{data.completed}</div>
          <div style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)' }}>Completed</div>
        </div>
        <div style={{ padding: '1rem', background: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)', minWidth: '120px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{data.notCompleted}</div>
          <div style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)' }}>Pending</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Completed</h3>
          <div style={{ background: 'var(--cr-bg-warm)', borderRadius: '8px', padding: '0.75rem', border: '1px solid var(--cr-border)' }}>
            {data.completedUsers?.length === 0 ? (
              <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>None yet</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {data.completedUsers?.map((u) => (
                  <li key={u.userId} style={{ marginBottom: '0.25rem' }}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'User'}
                    {u.email && (u.firstName || u.lastName) && (
                      <span style={{ color: 'var(--cr-text-muted)', fontSize: '0.85rem' }}> ({u.email})</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Not yet completed</h3>
          <div style={{ background: 'var(--cr-bg-warm)', borderRadius: '8px', padding: '0.75rem', border: '1px solid var(--cr-border)' }}>
            {data.notCompletedUsers?.length === 0 ? (
              <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>All done!</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {data.notCompletedUsers?.map((u) => (
                  <li key={u.userId} style={{ marginBottom: '0.25rem' }}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Pending user'}
                    {u.email && (u.firstName || u.lastName) && (
                      <span style={{ color: 'var(--cr-text-muted)', fontSize: '0.85rem' }}> ({u.email})</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <button
          type="button"
          className="btn-outline"
          onClick={() => (showAddSection ? setShowAddSection(false) : loadUsersNotInvited())}
          style={{ marginBottom: showAddSection ? '0.75rem' : 0 }}
        >
          {showAddSection ? 'Hide' : 'Add more invitees'}
        </button>
        {showAddSection && (
          <div style={{ padding: '1rem', background: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Users not yet invited</h3>
            {usersLoading && <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
            {!usersLoading && users.length === 0 && (
              <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>All users have been invited.</p>
            )}
            {!usersLoading && users.length > 0 && (
              <>
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '0.75rem' }}>
                  {users.map((u) => (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={addSelected.has(u.id)}
                        onChange={() => toggleAddUser(u.id)}
                      />
                      <span>
                        {u.firstName} {u.lastName}
                        {u.email && <span style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}> ({u.email})</span>}
                      </span>
                    </label>
                  ))}
                </div>
                {addError && <p style={{ color: 'var(--cr-error)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>{addError}</p>}
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAddInvitees}
                  disabled={adding || addSelected.size === 0}
                >
                  {adding ? 'Adding…' : `Add ${addSelected.size} invitee${addSelected.size !== 1 ? 's' : ''}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MinistryCheckupCompletionPage;
